/**
 * Utility for printing receipts and summaries in the POS system.
 * Uses window.print() via a hidden iframe for auto-print and manual print.
 */
import type { DadosImpressao } from '../../shared/types'

const NOME_RESTAURANTE = "MYRO'S CHURRASCARIA"

export interface ItemPrint {
  descricao: string
  qtd: number
  valor: number
  cancelado?: boolean
  pesoKg?: number
  valorQuilo?: number
  sequencial?: number
}

export interface DadosPrint {
  tipo: 'recibo' | 'resumo'
  mesa: number
  data: string
  contexto: string // "Mesa toda", "Comanda X", "Itens selecionados"
  comanda?: string
  sequencial?: number
  itens: ItemPrint[]
  subtotal: number
  descontos?: number
  adicionais?: number
  gorjeta?: number
  totalFinal: number
  totalPago?: number
  saldoRestante?: number
  codigoSaida?: string
  itensCancelados?: ItemPrint[]
  valorQuilo?: number
  diaSemana?: string
  atendente?: string
  horaAbertura?: string
  horaFechamento?: string
  codigoCliente?: string
}

function formatarMoeda(v: number): string {
  return `R$ ${v.toFixed(2)}`
}

function dataHoraAtual(): string {
  return new Date().toLocaleString('pt-BR')
}

/** Escape user-provided strings before interpolating into HTML to prevent XSS in the print window. */
function esc(s: any): string {
  if (s === undefined || s === null) return ''
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Generate HTML for printing */
function gerarHTML(dados: DadosPrint): string {
  const isRecibo = dados.tipo === 'recibo'
  const titulo = isRecibo ? 'RECIBO DE PAGAMENTO' : 'RESUMO'

  const itensHTML = dados.itens.map((i, idx) =>
    `<tr>
      <td style="text-align:left">
        ${i.sequencial ? `#${i.sequencial} ` : ''}${i.qtd}x ${esc(i.descricao)}
        ${i.pesoKg ? `<br><span style="font-size:12px">Peso: ${i.pesoKg.toFixed(3)}kg${i.valorQuilo ? ` × ${formatarMoeda(i.valorQuilo)}/kg` : ''}</span>` : ''}
      </td>
      <td style="text-align:right">${formatarMoeda(i.valor)}</td>
    </tr>`
  ).join('')

  const canceladosHTML = (dados.itensCancelados && dados.itensCancelados.length > 0)
    ? `<tr><td colspan="2" style="padding-top:8px;font-weight:bold;font-size:13px">CANCELADOS</td></tr>` +
      dados.itensCancelados.map(i =>
        `<tr style="text-decoration:line-through">
          <td style="text-align:left">${i.qtd}x ${esc(i.descricao)}</td>
          <td style="text-align:right">${formatarMoeda(i.valor)}</td>
        </tr>`
      ).join('')
    : ''

  const ajustesHTML = [
    dados.descontos && dados.descontos > 0 ? `<div style="display:flex;justify-content:space-between"><span>Desconto</span><span>-${formatarMoeda(dados.descontos)}</span></div>` : '',
    dados.adicionais && dados.adicionais > 0 ? `<div style="display:flex;justify-content:space-between"><span>Adicional</span><span>+${formatarMoeda(dados.adicionais)}</span></div>` : '',
    dados.gorjeta && dados.gorjeta > 0 ? `<div style="display:flex;justify-content:space-between"><span>Gorjeta</span><span>+${formatarMoeda(dados.gorjeta)}</span></div>` : '',
  ].filter(Boolean).join('')

  const pagamentoHTML = [
    dados.totalPago !== undefined && dados.totalPago > 0
      ? `<div style="display:flex;justify-content:space-between"><span>Já pago</span><span>${formatarMoeda(dados.totalPago)}</span></div>` : '',
    dados.saldoRestante !== undefined && dados.saldoRestante > 0
      ? `<div style="display:flex;justify-content:space-between"><span>Saldo restante</span><span>${formatarMoeda(dados.saldoRestante)}</span></div>` : '',
  ].filter(Boolean).join('')

  const codigoHTML = isRecibo && dados.codigoSaida
    ? `<div style="text-align:center;margin:12px 0;padding:10px;border:2px solid #000">
        <div style="font-size:11px;font-weight:bold">CÓDIGO DE SAÍDA</div>
        <div style="font-size:32px;font-weight:900;letter-spacing:6px;margin:6px 0">${dados.codigoSaida}</div>
        <div style="font-size:10px;font-weight:bold">Válido por 30 minutos — Apresente na saída</div>
      </div>`
    : ''

  const statusHTML = isRecibo
    ? '<div style="text-align:center;font-size:18px;font-weight:900;margin:10px 0;padding:6px;border:2px solid #000">*** PAGO ***</div>'
    : '<div style="text-align:center;font-size:12px;font-weight:bold;margin:6px 0;padding:5px;border:1px dashed #000">RESUMO — NÃO É DOCUMENTO FISCAL</div>'

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>${esc(titulo)} — Mesa ${esc(dados.mesa)}</title>
<style>
  @media print {
    body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { margin: 4mm; size: 80mm auto; }
    * { color: #000 !important; }
  }
  /* Tamanhos calibrados em 2026-05-08: meio termo entre o original (pequeno
     demais) e a versão anterior (grande, gastava papel). Térmico 80mm. */
  body {
    font-family: 'Arial Black', 'Helvetica Neue', Arial, sans-serif;
    font-size: 12px;
    font-weight: 600;
    max-width: 300px;
    margin: 0 auto;
    padding: 5px;
    color: #000;
    line-height: 1.3;
    -webkit-font-smoothing: none;
    text-rendering: geometricPrecision;
  }
  h1 { text-align: center; font-size: 16px; font-weight: 800; margin: 0 0 3px; letter-spacing: 1px; }
  h2 { text-align: center; font-size: 13px; margin: 0 0 6px; font-weight: 700; }
  .sep { border-top: 1.5px solid #000; margin: 4px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 2px 0; font-size: 12px; font-weight: 600; color: #000; }
  .total-row { font-size: 15px; font-weight: 800; }
  .footer { text-align: center; font-size: 10px; font-weight: 600; margin-top: 8px; }
  div { color: #000; }
  span { color: #000; }
</style>
</head><body>
  <h1>${NOME_RESTAURANTE}</h1>
  <h2>${esc(titulo)}</h2>
  <div class="sep"></div>
  <div style="display:flex;justify-content:space-between"><span>Mesa: ${esc(dados.mesa)}</span><span>${esc(dados.comanda || '')}${dados.codigoCliente ? ` (#${esc(dados.codigoCliente)})` : ''}</span></div>
  ${dados.sequencial ? `<div>Comanda #${esc(dados.sequencial)}</div>` : ''}
  ${dados.atendente ? `<div>Atendente: ${esc(dados.atendente)}</div>` : ''}
  <div>Data: ${esc(dados.data)}</div>
  ${dados.horaAbertura ? `<div>Abertura: ${esc(dados.horaAbertura)}</div>` : ''}
  ${dados.horaFechamento ? `<div>Fechamento: ${esc(dados.horaFechamento)}</div>` : ''}
  ${dados.valorQuilo ? `<div>Valor do quilo: ${formatarMoeda(dados.valorQuilo)}/kg${dados.diaSemana ? ` (${esc(dados.diaSemana)})` : ''}</div>` : ''}
  <div>Contexto: ${esc(dados.contexto)}</div>
  <div class="sep"></div>
  <table>${itensHTML}${canceladosHTML}</table>
  <div class="sep"></div>
  <div style="display:flex;justify-content:space-between"><span>Subtotal</span><span>${formatarMoeda(dados.subtotal)}</span></div>
  ${ajustesHTML}
  <div class="sep"></div>
  <div class="total-row" style="display:flex;justify-content:space-between"><span>TOTAL</span><span>${formatarMoeda(dados.totalFinal)}</span></div>
  ${pagamentoHTML}
  ${statusHTML}
  ${codigoHTML}
  <div class="footer">
    ${NOME_RESTAURANTE}<br>
    Obrigado pela preferência!
  </div>
</body></html>`
}

/**
 * Open a print window with formatted content.
 * Throws on popup-block so callers can surface the error to the user.
 *
 * Bug recorrente fix (2026-05-04): a versão antiga chamava `win.print()` no
 * `onload` E também no `setTimeout(500ms)` fallback. Em Chromium recente
 * `win.print()` chamado 2× NÃO lança erro — apenas imprime 2×, ignorando o
 * try/catch. Resultado: TODAS as impressões saíam duplicadas (fechar comanda,
 * dividir conta, reimprimir recibo, resumo, consolidado). Fix: flag de
 * "já imprimiu" garantindo que o segundo trigger seja no-op.
 */
export function imprimirConteudo(dados: DadosPrint): void {
  const html = gerarHTML(dados)
  const win = window.open('', '_blank', 'width=400,height=600')
  if (!win) {
    const msg = 'Janela de impressão bloqueada pelo navegador. Libere pop-ups para este site e tente novamente.'
    try { alert(msg) } catch {}
    throw new Error(msg)
  }
  win.document.write(html)
  win.document.close()
  // Garante 1 print apenas — onload OU setTimeout, nunca ambos.
  let alreadyPrinted = false
  const doPrint = () => {
    if (alreadyPrinted) return
    alreadyPrinted = true
    win.print()
  }
  win.onload = doPrint
  // Fallback genuíno (só dispara se onload não tiver disparado)
  setTimeout(doPrint, 500)
}

function mapLancToItem(l: any, idx: number, valorQuilo?: number): ItemPrint {
  // Calculate peso: use peso_gramas if available, otherwise derive from valor_total / valorQuilo
  let pesoKg: number | undefined
  if (l.peso_gramas) {
    pesoKg = l.peso_gramas / 1000
  } else if (valorQuilo && valorQuilo > 0 && l.codigo_balanca && Number(l.valor_total) > 0) {
    pesoKg = Number(l.valor_total) / valorQuilo
  }

  return {
    descricao: l.descricao,
    qtd: l.quantidade,
    valor: Number(l.valor_total),
    sequencial: idx + 1,
    pesoKg,
    valorQuilo: pesoKg && valorQuilo ? valorQuilo : undefined,
  }
}

/** Get the first comanda's attendant name and opening time */
function getAtendenteMesa(mesa: any): { atendente?: string; horaAbertura?: string } {
  const comandas = mesa.comandas || []
  if (comandas.length === 0) return {}
  // First comanda = who opened the mesa
  const primeira = comandas[0]
  return {
    atendente: primeira.criador_nome || undefined,
    horaAbertura: primeira.criada_em || undefined,
  }
}

/** Build DadosPrint from mesa state for a SUMMARY (manual, no fechamento) */
export function buildResumoMesa(mesa: any, valorQuilo?: number, diaSemana?: string): DadosPrint {
  const allLancs = mesa.comandas?.flatMap((c: any) => c.lancamentos || []) || []
  const ativos = allLancs.filter((l: any) => l.status === 'ativo')
  const cancelados = allLancs.filter((l: any) => l.status === 'cancelado')
  const { atendente, horaAbertura } = getAtendenteMesa(mesa)

  return {
    tipo: 'resumo',
    mesa: mesa.numero,
    data: dataHoraAtual(),
    contexto: 'Mesa toda',
    itens: ativos.map((l: any, i: number) => mapLancToItem(l, i, valorQuilo)),
    itensCancelados: cancelados.map((l: any, i: number) => mapLancToItem(l, i, valorQuilo)),
    subtotal: ativos.reduce((s: number, l: any) => s + Number(l.valor_total), 0),
    totalFinal: Number(mesa.total_atual) || ativos.reduce((s: number, l: any) => s + Number(l.valor_total), 0),
    totalPago: Number(mesa.total_pago) || 0,
    saldoRestante: Number(mesa.saldo_restante) || 0,
    valorQuilo,
    diaSemana,
    atendente,
    horaAbertura,
  }
}

/** Build DadosPrint from a specific comanda for SUMMARY */
export function buildResumoComanda(mesa: any, comanda: any, valorQuilo?: number, diaSemana?: string): DadosPrint {
  const lancs = comanda.lancamentos || []
  const ativos = lancs.filter((l: any) => l.status === 'ativo')
  const cancelados = lancs.filter((l: any) => l.status === 'cancelado')
  const subtotal = ativos.reduce((s: number, l: any) => s + Number(l.valor_total), 0)
  const { atendente, horaAbertura } = getAtendenteMesa(mesa)

  return {
    tipo: 'resumo',
    mesa: mesa.numero,
    data: dataHoraAtual(),
    contexto: `Comanda ${comanda.id}`,
    comanda: comanda.nome_cliente_opcional || `Comanda ${comanda.id}`,
    sequencial: comanda.id,
    itens: ativos.map((l: any, i: number) => mapLancToItem(l, i, valorQuilo)),
    itensCancelados: cancelados.map((l: any, i: number) => mapLancToItem(l, i, valorQuilo)),
    subtotal,
    totalFinal: subtotal,
    valorQuilo,
    diaSemana,
    atendente,
    horaAbertura,
    codigoCliente: comanda.codigo_cliente || undefined,
  }
}

/** Build DadosPrint from selected items for SUMMARY */
export function buildResumoItens(mesa: any, lancamentoIds: number[], valorQuilo?: number, diaSemana?: string): DadosPrint {
  const allLancs = mesa.comandas?.flatMap((c: any) => c.lancamentos || []) || []
  const selected = allLancs.filter((l: any) => lancamentoIds.includes(l.id))
  const ativos = selected.filter((l: any) => l.status !== 'cancelado')
  const cancelados = selected.filter((l: any) => l.status === 'cancelado')
  const subtotal = ativos.reduce((s: number, l: any) => s + Number(l.valor_total), 0)
  const { atendente, horaAbertura } = getAtendenteMesa(mesa)

  return {
    tipo: 'resumo',
    mesa: mesa.numero,
    data: dataHoraAtual(),
    contexto: `${ativos.length} item(ns) selecionado(s)`,
    itens: ativos.map((l: any, i: number) => mapLancToItem(l, i, valorQuilo)),
    itensCancelados: cancelados.map((l: any, i: number) => mapLancToItem(l, i, valorQuilo)),
    subtotal,
    totalFinal: subtotal,
    valorQuilo,
    diaSemana,
    atendente,
    horaAbertura,
  }
}

/** Build DadosPrint from a fechamento response for RECEIPT (auto-print) */
export function buildRecibo(mesa: any, fechamentoRes: any, valorQuilo?: number, diaSemana?: string, selectedComandaIds?: number[]): DadosPrint {
  const { atendente, horaAbertura } = getAtendenteMesa(mesa)

  // Filter comandas if specific ones are selected
  const allComandas: any[] = mesa.comandas || []
  const targetComandas = selectedComandaIds && selectedComandaIds.length > 0
    ? allComandas.filter((c: any) => selectedComandaIds.includes(c.id))
    : allComandas

  // For multiple selected comandas, discriminate by client
  const itens: ItemPrint[] = []
  let idx = 0
  if (targetComandas.length > 1 && selectedComandaIds && selectedComandaIds.length > 1) {
    for (const c of targetComandas) {
      const cLancs = (c.lancamentos || []).filter((l: any) => l.status === 'ativo' || l.status === 'pago')
      if (cLancs.length === 0) continue
      // Separator with client name and code
      const cLabel = `${c.nome_cliente_opcional || `Comanda ${c.id}`}${c.codigo_cliente ? ` #${c.codigo_cliente}` : ''}`
      itens.push({ descricao: `── ${cLabel} ──`, qtd: 0, valor: 0 })
      for (const l of cLancs) {
        idx++
        itens.push(mapLancToItem(l, idx - 1, valorQuilo))
      }
      const subComanda = cLancs.reduce((s: number, l: any) => s + Number(l.valor_total), 0)
      itens.push({ descricao: `Subtotal ${cLabel}`, qtd: 0, valor: subComanda })
    }
  } else {
    const allLancs = targetComandas.flatMap((c: any) => c.lancamentos || [])
    const ativos = allLancs.filter((l: any) => l.status === 'ativo' || l.status === 'pago')
    ativos.forEach((l: any, i: number) => itens.push(mapLancToItem(l, i, valorQuilo)))
  }

  // For single comanda, include its code
  const singleComandaCode = targetComandas.length === 1 && targetComandas[0].codigo_cliente
    ? targetComandas[0].codigo_cliente : undefined

  return {
    tipo: 'recibo',
    mesa: mesa.numero,
    data: dataHoraAtual(),
    contexto: fechamentoRes.tipo_fechamento === 'divisao' ? 'Divisão' :
              fechamentoRes.tipo_fechamento === 'mesa_total' ? 'Mesa toda' :
              fechamentoRes.tipo_fechamento === 'comanda' ? 'Comanda' : 'Itens',
    comanda: targetComandas.length === 1 ? (targetComandas[0].nome_cliente_opcional || undefined) : undefined,
    itens,
    subtotal: Number(fechamentoRes.subtotal) || 0,
    descontos: Number(fechamentoRes.descontos) || 0,
    adicionais: Number(fechamentoRes.adicionais) || 0,
    gorjeta: Number(fechamentoRes.gorjeta) || 0,
    totalFinal: Number(fechamentoRes.total_final),
    codigoSaida: fechamentoRes.codigo_saida_4_digitos,
    codigoCliente: singleComandaCode,
    valorQuilo,
    diaSemana,
    atendente,
    horaAbertura,
    horaFechamento: dataHoraAtual(),
  }
}

/** Build consolidated receipt for multiple closed comandas on one page */
export function buildReciboConsolidado(mesa: any, comandasFechadas: any[]): DadosPrint {
  // Build a single DadosPrint with all comandas combined but visually separated
  const allItens: ItemPrint[] = []
  let totalGeral = 0
  let idx = 0

  for (const c of comandasFechadas) {
    const lancs = (c.lancamentos || []).filter((l: any) => l.status !== 'cancelado')
    // Add separator item for each comanda (with code if available)
    const cLabel = `${c.nome_cliente_opcional || `Comanda ${c.id}`}${c.codigo_cliente ? ` #${c.codigo_cliente}` : ''}`
    allItens.push({
      descricao: `── ${cLabel} ──`,
      qtd: 0,
      valor: 0,
    })
    for (const l of lancs) {
      idx++
      allItens.push({
        descricao: l.descricao,
        qtd: l.quantidade,
        valor: Number(l.valor_total),
        sequencial: idx,
        pesoKg: l.peso_gramas ? l.peso_gramas / 1000 : undefined,
      })
    }
    const subComanda = lancs.reduce((s: number, l: any) => s + Number(l.valor_total), 0)
    allItens.push({
      descricao: `Subtotal ${cLabel}`,
      qtd: 0,
      valor: subComanda,
    })
    totalGeral += subComanda
  }

  const { atendente, horaAbertura } = getAtendenteMesa(mesa)

  return {
    tipo: 'resumo',
    mesa: mesa.numero,
    data: dataHoraAtual(),
    contexto: `${comandasFechadas.length} comanda(s) fechada(s) — Consolidado`,
    itens: allItens,
    subtotal: totalGeral,
    totalFinal: totalGeral,
    atendente,
    horaAbertura,
  }
}

/** Build DadosPrint for a single division part */
export function buildReciboDivisao(mesa: any, valor: number, codigoSaida: string, parteNum: number, totalPartes: number): DadosPrint {
  return {
    tipo: 'recibo',
    mesa: mesa.numero,
    data: dataHoraAtual(),
    contexto: `Divisão — Parte ${parteNum} de ${totalPartes}`,
    itens: [{ descricao: `Parte ${parteNum} da divisão`, qtd: 1, valor }],
    subtotal: valor,
    totalFinal: valor,
    codigoSaida,
  }
}

/**
 * Build DadosPrint from DadosImpressao (server response from GET /fechamentos/:id/recibo).
 * Used when reprinting from Histórico — adapts the flat server shape to the print DTO.
 */
export function buildReciboFromDados(dados: DadosImpressao): DadosPrint {
  return {
    tipo: 'recibo',
    mesa: dados.mesa_numero,
    data: dados.data_hora,
    contexto: dados.reimpressao ? 'Reimpressão' : 'Mesa toda',
    itens: dados.itens.map(i => ({
      descricao: i.descricao,
      qtd: i.qtd,
      valor: i.valor,
    })),
    subtotal: dados.subtotal,
    descontos: dados.descontos > 0 ? dados.descontos : undefined,
    adicionais: dados.adicionais > 0 ? dados.adicionais : undefined,
    gorjeta: dados.gorjeta > 0 ? dados.gorjeta : undefined,
    totalFinal: dados.total_final,
    codigoSaida: dados.codigo_saida || undefined,
    horaFechamento: dados.data_hora,
  }
}

/**
 * Imprime recibo de fechamento de caixa (D-102, 2026-05-16).
 *
 * Layout dedicado pro fechamento de turno — diferente do recibo de mesa.
 * Mostra: operador, abertura, fechamento, valor inicial, total vendido,
 * esperado, contado, diferença, observação + espaço para assinatura.
 */
export interface ReciboFechamentoCaixaInput {
  id: number
  operador: string
  valor_inicial_centavos: number
  abertura_em: string
  fechamento_em: string | null
  total_vendido_centavos: number
  /** D-112: separação por forma. Quando ausente, recibo cai pro layout antigo. */
  venda_dinheiro_centavos?: number
  outras_formas_centavos?: number
  /** D-119: totais de sangria/suprimento do turno. */
  sangrias_centavos?: number
  suprimentos_centavos?: number
  valor_final_centavos: number
  esperado_centavos: number
  diferenca_centavos: number
  observacao: string | null
  /** D-138: Z-Number sequencial (PDV-like). */
  z_numero_sequencial?: number | null
  /** D-138: motivo da diferença (preenchido quando diferenca ≠ 0). */
  diferenca_motivo?: string | null
}

export function imprimirReciboFechamentoCaixa(r: ReciboFechamentoCaixaInput): void {
  const fmt = (c: number) => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`
  const fmtDt = (s: string | null) => s ? s.replace('T', ' ').slice(0, 16) : '—'
  const diffSign = r.diferenca_centavos === 0 ? '' : r.diferenca_centavos > 0 ? '+' : '-'
  const diffAbs = fmt(Math.abs(r.diferenca_centavos))
  const diffLabel = r.diferenca_centavos === 0 ? 'igual' : r.diferenca_centavos > 0 ? 'sobra' : 'falta'

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Fechamento de Caixa #${r.id}</title>
<style>
  @media print {
    body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { margin: 4mm; size: 80mm auto; }
    * { color: #000 !important; }
  }
  body {
    font-family: 'Arial Black', 'Helvetica Neue', Arial, sans-serif;
    font-size: 12px; font-weight: 600; max-width: 300px; margin: 0 auto;
    padding: 5px; color: #000; line-height: 1.4;
  }
  h1 { text-align: center; font-size: 16px; font-weight: 800; margin: 0 0 3px; letter-spacing: 1px; }
  h2 { text-align: center; font-size: 13px; margin: 0 0 6px; font-weight: 700; }
  .sep { border-top: 1.5px solid #000; margin: 5px 0; }
  .row { display: flex; justify-content: space-between; padding: 1.5px 0; }
  .total-row { font-size: 14px; font-weight: 800; }
  .assinatura { margin-top: 22px; text-align: center; font-size: 11px; }
  .linha-assinatura { border-top: 1.5px solid #000; margin-top: 36px; padding-top: 3px; }
</style>
</head><body>
  <h1>${NOME_RESTAURANTE}</h1>
  <h2>FECHAMENTO Z ${r.z_numero_sequencial ? `#${r.z_numero_sequencial}` : `(turno ${r.id})`}</h2>
  <div class="sep"></div>
  <div class="row"><span>Operador:</span><span>${esc(r.operador)}</span></div>
  <div class="row"><span>Aberto em:</span><span>${esc(fmtDt(r.abertura_em))}</span></div>
  <div class="row"><span>Fechado em:</span><span>${esc(fmtDt(r.fechamento_em))}</span></div>
  <div class="sep"></div>
  <div style="font-size:10px;font-weight:700;text-align:center;margin:3px 0">VENDAS NO TURNO</div>
  <div class="row total-row"><span>Total vendido</span><span>${fmt(r.total_vendido_centavos)}</span></div>
  ${(r.venda_dinheiro_centavos !== undefined && r.venda_dinheiro_centavos > 0) ? `
  <div class="row" style="padding-left:10px;font-size:11px"><span>• em dinheiro</span><span>${fmt(r.venda_dinheiro_centavos)}</span></div>
  <div class="row" style="padding-left:10px;font-size:11px"><span>• em PIX/cartão</span><span>${fmt(r.outras_formas_centavos ?? 0)}</span></div>
  ` : `<div style="font-size:9.5px;color:#333;margin-top:2px">débito / crédito / PIX — forma por transação em desenvolvimento</div>`}
  <div class="sep"></div>
  <div style="font-size:10px;font-weight:700;text-align:center;margin:3px 0">FUNDO DE TROCO (dinheiro físico)</div>
  <div class="row"><span>Abertura</span><span>${fmt(r.valor_inicial_centavos)}</span></div>
  ${(r.suprimentos_centavos ?? 0) > 0 ? `<div class="row"><span>+ Suprimentos</span><span>+ ${fmt(r.suprimentos_centavos ?? 0)}</span></div>` : ''}
  ${(r.sangrias_centavos ?? 0) > 0 ? `<div class="row"><span>− Sangrias</span><span>− ${fmt(r.sangrias_centavos ?? 0)}</span></div>` : ''}
  <div class="row total-row"><span>Esperado</span><span>${fmt(r.esperado_centavos)}</span></div>
  <div class="row total-row"><span>Contado</span><span>${fmt(r.valor_final_centavos)}</span></div>
  <div class="row total-row"><span>Diferença</span><span>${diffSign}${diffAbs} (${diffLabel})</span></div>
  ${r.diferenca_motivo ? `<div style="font-size:11px;margin-top:3px"><b>Motivo:</b> ${esc(r.diferenca_motivo)}</div>` : ''}
  ${r.observacao ? `<div class="sep"></div><div style="font-size:11px"><b>Obs:</b> ${esc(r.observacao)}</div>` : ''}
  <div class="assinatura">
    <div class="linha-assinatura">Assinatura do operador</div>
  </div>
  <div style="text-align:center;font-size:9px;margin-top:8px;color:#555">RESUMO Z — NÃO É DOCUMENTO FISCAL<br/>NFC-e a implementar</div>
</body></html>`

  const win = window.open('', '_blank', 'width=400,height=600')
  if (!win) {
    const msg = 'Janela de impressão bloqueada. Libere pop-ups para imprimir o recibo de fechamento.'
    try { alert(msg) } catch {}
    throw new Error(msg)
  }
  win.document.write(html)
  win.document.close()
  let alreadyPrinted = false
  const doPrint = () => {
    if (alreadyPrinted) return
    alreadyPrinted = true
    win.print()
  }
  win.onload = doPrint
  setTimeout(doPrint, 500)
}
